/**
 * Lenz — búsqueda predictiva del header (drawer).
 *
 * theme.js es generado (no editable), así que parcheamos el prototipo del
 * custom element <predictive-search> y reemplazamos `_doPredictiveSearch`.
 * Reutilizamos `_transitionToSlot`, `_queryInput`, `_abortController` y el
 * atributo `section-id` que ya define theme.js.
 *
 * Qué cambia respecto a theme.js (auditoría 2026-10-24, busqueda.md #1, #3, #4):
 *  1. Pedimos 20 productos en vez de 10, ordenamos DISPONIBLES primero
 *     (los "Agotado" al final), dentro de cada grupo por relevancia del
 *     título, y recortamos a MAX_ITEMS.
 *  2. Los textos con `terms` (estado vacío y status sr-only) muestran lo que
 *     escribió el cliente y no la query expandida (`sd (128 OR 128mm OR 128cm)`).
 *  3. Sin resultados: el estado vacío se pinta al instante con la primera
 *     respuesta. Los fallbacks (OR de 36 prefijos solo para tokens cortos con
 *     dígito; /search estándar que corrige typos) corren después y, si traen
 *     algo, reemplazan el estado vacío.
 */
(function () {
  'use strict';
  if (!window.customElements || typeof fetch !== 'function') return;

  var FIELDS = 'title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag';
  var FETCH_LIMIT = 20;   // pool para reordenar
  var MAX_ITEMS = 10;     // lo que se muestra por pestaña
  var CACHE_MAX = 40;
  var cache = new Map();

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchText(url, signal) {
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    return fetch(url, { signal: signal }).then(function (res) {
      if (res.status !== 200) throw new Error('HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
      cache.set(url, text);
      return text;
    });
  }

  function fetchSlot(url, signal) {
    return fetchText(url, signal).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('[slot="results"]');
    });
  }

  function params(q, sectionId) {
    return 'q=' + q + '&section_id=' + encodeURIComponent(sectionId) +
      '&resources[limit]=' + FETCH_LIMIT + '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=show' +
      '&resources[options][fields]=' + FIELDS;
  }

  function isEmpty(slot) {
    return !slot || !!slot.querySelector('.empty-state') || !slot.querySelector('a.predictive-search-result, a[data-instant]');
  }

  // "100" → "(100 OR 100mm OR 100cm)" (misma expansión que theme.js).
  function expandQuery(q) {
    return q.trim().split(/\s+/).map(function (t) {
      return /^\d+$/.test(t) ? '(' + t + ' OR ' + t + 'mm OR ' + t + 'cm)' : t;
    }).join(' ');
  }

  // Fallback OR de prefijos: solo tiene sentido para fragmentos de modelo
  // ("6400" → a6400, "663" → 6663A): un token de 2-6 chars con algún dígito.
  function wantsPrefixFallback(q) {
    return /^[a-z0-9-]{2,6}$/i.test(q) && /\d/.test(q);
  }

  function prefixOrQuery(q) {
    var chars = '0123456789abcdefghijklmnopqrstuvwxyz'.split('');
    return [encodeURIComponent(q)].concat(chars.map(function (c) { return encodeURIComponent(c + q); })).join('+OR+');
  }

  function tokensOf(q) {
    return q.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(function (t) { return t.length >= 2; });
  }

  // Disponibles primero; dentro de cada grupo, más tokens de la query en el
  // título primero; empate = orden original de Shopify. Recorta a MAX_ITEMS.
  function reorder(slot, q) {
    if (!slot) return;
    var tokens = tokensOf(q);
    var full = q.trim().toLowerCase();
    slot.querySelectorAll('.predictive-search__tab-content').forEach(function (container) {
      var items = Array.prototype.slice.call(container.querySelectorAll(':scope > a.predictive-search-result, :scope > a[data-instant]'));
      if (!items.length) return;
      var isProducts = items.some(function (a) { return a.href && a.href.indexOf('/products/') !== -1; });
      if (isProducts && items.length > 1) {
        var scored = items.map(function (item, index) {
          var titleEl = item.querySelector('.bold .reversed-link, .bold');
          var title = (titleEl ? titleEl.textContent : '').toLowerCase();
          var score = 0;
          tokens.forEach(function (t) { if (title.indexOf(t) !== -1) score += 1; });
          if (full && title.indexOf(full) !== -1) score += tokens.length;
          var soldOut = !!item.querySelector('.badge--sold-out');
          return { item: item, score: score, soldOut: soldOut, index: index };
        });
        scored.sort(function (a, b) {
          if (a.soldOut !== b.soldOut) return a.soldOut ? 1 : -1;
          if (b.score !== a.score) return b.score - a.score;
          return a.index - b.index;
        });
        items = scored.map(function (s) { return s.item; });
        items.forEach(function (item) { container.appendChild(item); });
      }
      items.slice(MAX_ITEMS).forEach(function (item) { item.remove(); });
    });
  }

  // Sustituye la query interna (expandida / OR / corregida) por lo tipeado en
  // los textos que Shopify rellena con `terms`.
  function showTypedTerms(slot, q) {
    if (!slot) return;
    var typed = q.trim();
    slot.querySelectorAll('.empty-state p, .sr-only[role="status"]').forEach(function (el) {
      var text = el.textContent;
      var fixed = text.replace(/(«)([\s\S]*)(»)/, '$1' + typed.replace(/\$/g, '$$$$') + '$3');
      if (fixed === text) fixed = text.replace(/(")([\s\S]*)(")/, '$1' + typed.replace(/\$/g, '$$$$') + '$3');
      if (fixed !== text) el.textContent = fixed;
    });
  }

  function polish(slot, q) {
    reorder(slot, q);
    showTypedTerms(slot, q);
    return slot;
  }

  function isAbort(err) {
    return err && err.name === 'AbortError';
  }

  function patch(Cls) {
    var proto = Cls.prototype;
    if (!proto || proto.__lenzPredictive) return;
    proto.__lenzPredictive = true;

    proto._doPredictiveSearch = async function () {
      var self = this;
      var signal = self._abortController ? self._abortController.signal : undefined;
      var q = self._queryInput.value;
      var trimmed = q.trim();
      var sectionId = self.getAttribute('section-id');
      if (!trimmed || !sectionId) return self._transitionToSlot('idle');

      function render(slot) {
        if (signal && signal.aborted) return;
        var current = self.querySelector('[slot="results"]');
        if (!current || !slot) return;
        current.replaceWith(document.importNode(slot, true));
        return self._transitionToSlot('results');
      }

      try {
        await self._transitionToSlot('loading');

        var primary = await fetchSlot(root() + 'search/suggest?' + params(encodeURIComponent(expandQuery(q)), sectionId), signal);
        if (!primary) return self._transitionToSlot('idle');

        if (!isEmpty(primary)) {
          return render(polish(primary, q));
        }

        // Sin resultados: estado vacío YA (con "Quizás te interese"), y recién
        // después intentamos recuperar algo con los fallbacks.
        await render(polish(primary, q));

        if (wantsPrefixFallback(trimmed)) {
          var alt = await fetchSlot(root() + 'search/suggest?' + params(prefixOrQuery(trimmed), sectionId), signal);
          if (!isEmpty(alt)) return render(polish(alt, q));
        }

        // /search estándar: motor más permisivo (corrige "sonny" → Sony).
        var std = await fetchSlot(root() + 'search?' + params(encodeURIComponent(trimmed), sectionId), signal);
        if (!isEmpty(std)) return render(polish(std, q));
      } catch (err) {
        if (isAbort(err)) return;
        // Red / 429: no dejamos el esqueleto colgado.
        if (!(signal && signal.aborted)) self._transitionToSlot('idle');
        if (window.console && console.warn) console.warn('[lenz-search-predictive]', err);
      }
    };
  }

  var existing = window.customElements.get('predictive-search');
  if (existing) patch(existing);
  else window.customElements.whenDefined('predictive-search').then(function () {
    patch(window.customElements.get('predictive-search'));
  });
})();
